import { useMemo, type ReactElement } from 'react';
import type { SupplyLight, SupplySummary } from '@wwf/sdk';
import { Box, Link, Stack, Typography } from '@mui/material';
import { useRecordContext } from 'react-admin';
import { useSupplyDrawer } from '@app/supply/drawer/SupplyDrawer';
import { SupplyAvatar } from './SupplyAvatar';
import { isDefined } from '@core/utils/assertions';

export const SupplyLightCard = (props: {
    record?: SupplyLight | SupplySummary;
    compressed?: boolean;
    noLink?: boolean;
}) => {
    const record = useRecordContext<SupplyLight | SupplySummary>(props);
    const { openDrawer } = useSupplyDrawer();

    const description = useMemo(() => {
        return [
            record?.type,
            record?.target,
            record?.price != null
                ? new Intl.NumberFormat(undefined, {
                      style: 'currency',
                      currency: 'EUR',
                  }).format(Number.parseFloat(record.price ?? '0'))
                : null,
        ]
            .filter(isDefined)
            .join(' • ');
    }, [record?.type, record?.target, record?.price]);

    const content = (
        <Box display="flex" alignItems="center" gap={0.5}>
            <SupplyAvatar variant="sm" record={record} />
            <Stack direction="column" gap={0.5}>
                <Typography
                    variant="body2"
                    noWrap
                    sx={
                        props.compressed
                            ? { fontSize: '0.8rem', lineHeight: 1.2 }
                            : undefined
                    }
                >
                    {record?.name}
                </Typography>
                <Typography variant="body2" noWrap>
                    {description}
                </Typography>
            </Stack>
        </Box>
    );

    const wrap = (node: ReactElement) => node;

    if (props.noLink) return wrap(content);

    if (record?.id != null) {
        return wrap(
            <Link
                component="button"
                variant="body2"
                onClick={() => openDrawer('show', record.id)}
                sx={{
                    cursor: 'pointer',
                    textAlign: 'left',
                    border: 0,
                    background: 'none',
                    padding: 0,
                    font: 'inherit',
                }}
            >
                {content}
            </Link>
        );
    }

    return wrap(content);
};
